import { Link } from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";
import { constants } from "../../constants";
import Head from "next/head";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games",
    description: "Select the games you wish to attend.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to Account Manager",
    description:
      "Click Continue and connect to your Pittsburgh Penguins account using our secure Ticketmaster login.",
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for the games.",
  },
  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your Pittsburgh Penguins{" "}
        <Link
          href={"https://am.ticketmaster.com/penguins/"}
          target={"_blank"}
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          Account Manager
        </Link>{" "}
        within 24 hours of purchase. Reach out to Live Fan Support for any
        questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "How do the Pittsburgh Penguins Flex Plans work?",
    description:
      "The Pittsburgh Penguins Flex Plan lets you fully customize your experience. Choose the games that best fit your schedule and your seats - giving you flexibility and control over your season.",
  },
  {
    title: "Do I have to select all my games now?",
    description: "Yes, you must select all games at the time of purchase.",
  },
  {
    title: "Can I choose seats throughout the venue?",
    description: "Yes, you can choose from all current available inventory.",
  },
  {
    title: "Where are my tickets delivered?",
    description: (
      <>
        Event tickets are delivered directly within your Pittsburgh Penguins{" "}
        <Link
          href={"https://am.ticketmaster.com/penguins/"}
          target={"_blank"}
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          Account Manager
        </Link>
        .
      </>
    ),
  },
];

export const CheckoutTerms = () => (
  <>
    By purchasing you agree to receive SMS alerts regarding ticket delivery and
    agree to the{" "}
    <Link
      href="https://www.attend.tech/terms-of-use"
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
    >
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link
      href="https://www.attend.tech/privacy-policy"
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
    >
      Privacy Policy
    </Link>
    .
  </>
);

export const RegistrationTerms = () => (
  <>
    By checking this box you agree to receive notifications and offers from the
    Pittsburgh Penguins and to the{" "}
    <Link
      href="https://www.attend.tech/terms-of-use"
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
    >
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link
      href="https://www.attend.tech/privacy-policy"
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
    >
      Privacy Policy
    </Link>
    .
  </>
);

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const { teamSchema } = props.teamConfig;

  return (
    <>
      <Head>
        <script
          nonce={process.env.pixelScriptNonce}
          id={`${teamSchema}-meta-pixel-script`}
          defer
          dangerouslySetInnerHTML={{
            __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '289584548067369');
              fbq('track', 'PageView');
              console.log('meta pixel page load event fired.');
              window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function(e) {
                try {
                  const { cartRes, checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    value: String(cartRes.allInPrice), 
                    currency: 'USD', 
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error); 
                }
              });
            `,
          }}
        />

        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=289584548067369&ev=PageView&noscript=1"
          />
        </noscript>
      </Head>
    </>
  );
};
